import { FlowDetail } from "@/models/flows";
import { Info } from "lucide-react";
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useState } from "react";
import { BASE_URL } from "@/dashboardQueries";

interface Props {
  flow: FlowDetail;
  isLoading?: boolean;
  onBackfill?: (ids: number[]) => void;
}

export const BackfillAlert = ({
  flow,
  isLoading: propLoading,
  onBackfill,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (submissionIds: number[]) => {
      setLoading(true);
      const res = await axios.post(
        `${BASE_URL}/flows/${flow.flow.id}/backfill`,
        { submission_ids: submissionIds }
      );
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("Backfill scheduled for submission successfully");
        queryClient.invalidateQueries(["flow", flow.flow.id.toString()]);
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to start backfill"
        );
      },
      onSettled: () => {
        setLoading(false);
      },
    }
  );

  const dismissMutation = useMutation(
    async () => {
      const res = await axios.post(
        `${BASE_URL}/flows/${flow.flow.id}/dismiss-backfill`
      );
      return res.data;
    },
    {
      onSuccess: () => {
        toast.success("Backfill dismissed successfully");
        queryClient.invalidateQueries(["flow", flow.flow.id.toString()]);
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to dismiss backfill"
        );
      },
    }
  );

  const handleBackfill = () => {
    if (onBackfill) {
      onBackfill(flow.backfillableIds);
    } else {
      mutation.mutate(flow.backfillableIds);
    }
  };

  return (
    <div className="flex bg-blue-900 border border-blue-600/30 py-2 px-4 rounded-lg shadow-sm mb-4 justify-between">
      <div className="flex items-center">
        <Info className="h-5 w-5 text-blue-200 mr-2" />
        <div className="text-blue-200 text-sm">
          <span className="font-bold">
            {flow?.backfillableIds?.length} form submission
            {flow?.backfillableIds?.length > 1 ? "s" : ""}{" "}
          </span>{" "}
          existed from before this flow created
        </div>
      </div>
      <div className="flex gap-2">
        <button
          className="text-sm text-blue-500 hover:text-blue-400 mr-2 underline transition duration-300"
          onClick={() => dismissMutation.mutate()}
        >
          Dismiss
        </button>
        <button
          className="text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-1 border-blue-600/30"
          onClick={handleBackfill}
          disabled={loading || propLoading}
        >
          {loading || propLoading ? "Running..." : "Run Backfill"}
        </button>
      </div>
    </div>
  );
};
